import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const TourCtx = createContext(null);

/**
 * Tour step shape:
 *  - route?: optional path the tour should navigate to before showing this step
 *  - matchRoute?: optional regex that must match current pathname (use for dynamic routes like /negotiation/:id)
 *  - target: data-testid or full CSS selector of the element to highlight
 *  - title, body: copy
 *  - placement?: "top" | "bottom" | "left" | "right" (default "bottom")
 *  - requiresExisting?: function(api) => boolean — if returns false, step is skipped (e.g. requires at least one draft)
 *  - prelude?: async function(navigate) — runs before the step, e.g. open a dialog
 */
export const TOUR_STEPS = [
  {
    route: "/negotiation",
    target: '[data-testid="neg-new-btn"]',
    title: "Step 1 · Upload sponsor draft",
    body: "Click this button to upload a sponsor's draft CTA (PDF / DOCX / TXT). We extract every line item AND auto-run a 23-category contract-language review in the background.",
    placement: "bottom-end",
  },
  {
    route: "/negotiation",
    target: '[data-testid^="neg-row-"]',
    title: "Step 2 · Your active drafts",
    body: "Every draft you upload lands here. Each row shows live Sponsor $ vs Counter $ and delta. Click any row's Open button to start editing.",
    placement: "right",
    skipIfMissing: true,
  },
  {
    matchRoute: /^\/negotiation\/[^/]+$/,
    target: '[data-testid="neg-tab-review"]',
    title: "Step 3 · Contract Language Review (inline)",
    body: "Once inside a negotiation, this tab shows the AI's clause-by-clause review. Each risky clause has a priority badge, escalation flag, and proposed counter-language.",
    placement: "bottom",
    requiresEditor: true,
  },
  {
    matchRoute: /^\/negotiation\/[^/]+$/,
    target: '[data-testid="bench-cell"], [data-testid="bench-nodata"]',
    title: "Step 4 · Inline benchmark percentiles",
    body: "Every line item shows live P75 / P80 / P90 from your signed-CTA pool. Click any percentile chip to instantly match the Stryde Counter to that value.",
    placement: "left",
    requiresEditor: true,
  },
  {
    matchRoute: /^\/negotiation\/[^/]+$/,
    target: '[data-testid="neg-finalize-btn"]',
    title: "Step 5 · Finalize when deal closes",
    body: "When you reach final terms, click here. Your Stryde Counter values become a SIGNED CTA that joins the benchmark pool — sharpening percentiles for every future negotiation.",
    placement: "bottom-end",
    requiresEditor: true,
  },
  {
    route: "/cta",
    target: '[data-testid="cta-filter-signed"]',
    title: "Step 6 · CTA Library is signed-only by default",
    body: "Your signed contracts live here — and only signed ones feed Benchmark + Rate Card. Toggle to Drafts when you need to manage in-flight contracts.",
    placement: "bottom",
  },
  {
    route: "/cta",
    target: '[data-testid^="cta-status-"]',
    title: "Step 7 · Flip status anytime",
    body: "Need to manually mark a CTA as Draft (to exclude from benchmark) or Signed (to include)? Click any status dropdown.",
    placement: "left",
    skipIfMissing: true,
  },
];

export function TourProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [active, setActive] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);

  const step = active ? TOUR_STEPS[stepIdx] : null;

  // Ensure route is correct for the current step
  useEffect(() => {
    if (!active || !step) return;
    if (step.route && location.pathname !== step.route) {
      navigate(step.route);
    }
  }, [active, step, location.pathname, navigate]);

  const start = useCallback(() => {
    setStepIdx(0);
    setActive(true);
  }, []);

  const stop = useCallback(() => {
    setActive(false);
    setStepIdx(0);
  }, []);

  const next = useCallback(() => {
    setStepIdx((i) => {
      const ni = i + 1;
      if (ni >= TOUR_STEPS.length) {
        setActive(false);
        return 0;
      }
      const nextStep = TOUR_STEPS[ni];
      // If next step requires editor and user isn't on one, try to open the first draft
      if (nextStep.requiresEditor && !/^\/negotiation\/[^/]+$/.test(location.pathname)) {
        const firstOpen = document.querySelector('[data-testid^="neg-open-"]');
        if (firstOpen) {
          firstOpen.click();
        } else {
          // No draft to open — skip all editor steps
          let skip = ni;
          while (skip < TOUR_STEPS.length && TOUR_STEPS[skip].requiresEditor) skip += 1;
          return skip < TOUR_STEPS.length ? skip : (setActive(false), 0);
        }
      }
      return ni;
    });
  }, [location.pathname]);

  const prev = useCallback(() => {
    setStepIdx((i) => Math.max(0, i - 1));
  }, []);

  const value = useMemo(
    () => ({ active, step, stepIdx, total: TOUR_STEPS.length, start, stop, next, prev }),
    [active, step, stepIdx, start, stop, next, prev]
  );

  return <TourCtx.Provider value={value}>{children}</TourCtx.Provider>;
}

export function useTour() {
  return useContext(TourCtx) || { active: false, step: null, start: () => {}, stop: () => {}, next: () => {}, prev: () => {} };
}
